import { useDraggable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { SchedaCalendario } from "../../api/calendario";

/** Prefisso degli id delle schede senza data, distinto da quelle già collocate. */
export const DRAG_SCHEDA_PREFIX = "scheda:";

/**
 * Riga della sezione "Senza data" del calendario unificato, trascinabile su un
 * giorno per assegnarle una data.
 *
 * La maniglia è un elemento a sé: senza, il sensore intercetterebbe il click
 * che apre il dettaglio al primo pixel di movimento. È anche il punto su cui
 * agisce la tastiera — riceve il focus e con Spazio/Invio avvia lo
 * spostamento, poi le frecce scelgono il giorno.
 *
 * Trascinabile solo se il backend ha dichiarato la scheda modificabile
 * (`can_edit`): le regole di autorizzazione sono quattro, una per modulo, e
 * ricalcolarle qui le farebbe divergere da quelle vere.
 */
export function SchedaTrascinabile({
  scheda,
  onApri,
  children,
}: {
  scheda: SchedaCalendario;
  onApri: () => void;
  children: React.ReactNode;
}) {
  const trascinabile = scheda.can_edit;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${DRAG_SCHEDA_PREFIX}${scheda.tipo}:${scheda.id}`,
    data: { scheda },
    disabled: !trascinabile,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-2 border-b border-line last:border-b-0 ${isDragging ? "opacity-40" : ""}`}
    >
      {trascinabile && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          aria-label={`Sposta ${scheda.titolo} su un giorno del calendario`}
          className="shrink-0 cursor-grab touch-none rounded p-1.5 text-ink/25 transition-colors hover:bg-black/5 hover:text-ink/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* Bloccata da una regola di dominio (non da un permesso): l'appiglio
          resta visibile ma spento, con il motivo a portata di puntatore. Senza,
          il gesto semplicemente non risponderebbe e sembrerebbe un guasto. */}
      {!trascinabile && scheda.motivo_blocco && (
        <span
          role="img"
          title={scheda.motivo_blocco}
          aria-label={`${scheda.titolo}: ${scheda.motivo_blocco}`}
          className="shrink-0 cursor-not-allowed rounded p-1.5 text-ink/15"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      <button type="button" onClick={onApri} className="min-w-0 flex-1 py-2 text-left">
        {children}
      </button>
    </div>
  );
}
